import { injectScript } from '@/helpers/dom'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { getIosTrackingAuthorization } from '@/services/capacitor.trackingAuthorizationPlugin'

/**
 * Initialize the consent mode handling.
 *
 * It has a slightly different behavior depending on the app's platform:
 * - Web: Load Termly.
 * - Android: Set all consent settings to `granted` (we assume implicit consent
 *   from the app's description in the Play Store).
 * - iOS: Get the default consent setting from local storage, and set it to
 *   `denied` if it doesn't exist. Then, in the app's code, it updates the consent
 *   settings based on the `ATTrackingManager`'s tracking permission.
 */
export function initializeConsent(): void {
  const isAndroid = navigator.userAgent.includes('shortform-mobile:android')
  if (isAndroid) {
    setConsent('default', 'granted')
    return
  }

  const isIOS = navigator.userAgent.includes('shortform-mobile:ios')
  if (isIOS) {
    const storedConsent = localStorage.getItem(StorageKeys.iosConsentSetting)
    if (storedConsent === 'granted' || storedConsent === 'denied') {
      setConsent('default', storedConsent)
    } else {
      setConsent('default', 'denied')
    }
    return
  }

  // In the web, we use Termly to handle consent mode, but we still need to set the
  // default consent options while it loads.
  // Default to granted outside of GDPR countries.
  setConsent('default', 'granted')
  setConsent('default', 'denied', {
    wait_for_update: 500,
    // Default to denied on GDPR countries.
    // prettier-ignore
    region: [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'IS', 'LI',
      'NO'
    ],
  })
  injectScript(
    'https://app.termly.io/resource-blocker/488c340e-3aec-4753-b00c-f7a5f771235c',
    { onload: onTermlyLoaded },
  )
}

/**
 * Sync the GTM consent settings with Microsoft's UET consent settings.
 */
export function onTermlyLoaded(): void {
  if (typeof Termly === 'undefined') {
    // This should never happen on production, because if this function was called,
    // it means that the Termly script was loaded; but it can happen in the JSDOM
    // environment during the build process.
    return
  }

  // Check if the user has already consented before
  // (outside of the EU, it should always return `true`).
  const alreadyConsented = Termly.getConsentState()?.advertising === true

  // Sync the consent setting with Microsoft UET.
  window.uetq = window.uetq || []
  window.uetq.push('consent', 'update', {
    ad_storage: alreadyConsented ? 'granted' : 'denied',
  })

  // This event is triggered when the consent mode changes.
  // We use it to keep the UET consent mode in sync with the
  // GTM consent mode.
  Termly.on('consent', (data: any) => {
    const hasConsented = data?.consentState?.advertising === true

    window.uetq = window.uetq || []
    window.uetq.push('consent', 'update', {
      ad_storage: hasConsented ? 'granted' : 'denied',
    })
  })
}

function setConsent(
  eventType: 'default' | 'update',
  consentValue: 'granted' | 'denied',
  additionalData?: Record<string, any>,
): void {
  gtag('consent', eventType, {
    ad_personalization: consentValue,
    ad_storage: consentValue,
    ad_user_data: consentValue,
    analytics_storage: consentValue,
    functionality_storage: consentValue,
    personalization_storage: consentValue,
    social_storage: consentValue,
    unclassified_storage: consentValue,
    security_storage: 'granted',
    ...additionalData,
  })

  window.uetq = window.uetq || []
  window.uetq.push('consent', eventType, {
    ad_storage: consentValue,
  })
}

/**
 * Update the GTM consent setting based on the iOS' tracking permission setting.
 */
export async function updateConsentOnIOS(): Promise<void> {
  const trackingAuthorizationStatus = await getIosTrackingAuthorization()
  if (trackingAuthorizationStatus === null) {
    // Null here means that the code is not running in the native iOS app,
    // so we just return.
    return
  }

  if (trackingAuthorizationStatus === 'notDetermined') {
    // The user has not answered the tracking authorization prompt.
    // The native app code will trigger an event when that happens.
    document.addEventListener('trackingPermissionChanged', (data: any) => {
      const authorizationStatus = data.authorized ? 'granted' : 'denied'
      setConsent('update', authorizationStatus)
      storage.setItem(StorageKeys.iosConsentSetting, authorizationStatus)
    })
  }

  if (['authorized', 'notRequired'].includes(trackingAuthorizationStatus)) {
    setConsent('update', 'granted')
    storage.setItem(StorageKeys.iosConsentSetting, 'granted')
  }

  if (['denied', 'restricted'].includes(trackingAuthorizationStatus)) {
    setConsent('update', 'denied')
    storage.setItem(StorageKeys.iosConsentSetting, 'denied')
  }
}
